import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCrypto } from '../context/CryptoContext';
import CoinSelector from '../components/CoinSelector';
import {
    LuTarget,
    LuTrendingUp,
    LuCalendar,
    LuDollarSign,
    LuPercent,
    LuPlus,
    LuTrash2,
    LuCheck,
    LuClock,
    LuPiggyBank,
    LuChartLine,
    LuSparkles,
    LuBitcoin,
    LuRepeat,
    LuShield,
    LuFlame,
    LuSnowflake,
    LuActivity,
    LuZap
} from 'react-icons/lu';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

function GoalPlanning({ mode = 'fiat' }) {
    const { lang } = useLanguage();
    const { coins, connected } = useCrypto();

    const isCrypto = mode === 'crypto';

    const [goals, setGoals] = useState(() => {
        const key = isCrypto ? 'cryptoGoals' : 'investmentGoals';
        const saved = localStorage.getItem(key);
        return saved ? JSON.parse(saved) : [];
    });

    const [showForm, setShowForm] = useState(false);
    const [newGoal, setNewGoal] = useState({
        name: '',
        targetAmount: '',
        targetCoinAmount: '',
        currentAmount: '',
        currentCoinAmount: '',
        monthlyContribution: '',
        weeklyCoinAmount: '',
        expectedReturn: isCrypto ? '0' : '8',
        deadline: '',
        category: isCrypto ? 'hodl' : 'savings',
        selectedCoin: 'BTCUSDT',
        priceScenario: 'neutral'
    });

    // Crypto categories
    const cryptoCategories = [
        { id: 'hodl', label: lang === 'tr' ? 'HODL (Birikim)' : 'HODL (Accumulate)', icon: LuPiggyBank },
        { id: 'dca', label: lang === 'tr' ? 'DCA Planı' : 'DCA Plan', icon: LuRepeat },
        { id: 'trading', label: lang === 'tr' ? 'Trading Kârı' : 'Trading Profit', icon: LuTrendingUp },
        { id: 'staking', label: lang === 'tr' ? 'Staking Geliri' : 'Staking Income', icon: LuShield },
    ];

    // Fiat categories
    const fiatCategories = [
        { id: 'savings', label: lang === 'tr' ? 'Birikim' : 'Savings', icon: LuPiggyBank },
        { id: 'retirement', label: lang === 'tr' ? 'Emeklilik' : 'Retirement', icon: LuCalendar },
        { id: 'investment', label: lang === 'tr' ? 'Yatırım' : 'Investment', icon: LuTrendingUp },
        { id: 'other', label: lang === 'tr' ? 'Diğer' : 'Other', icon: LuTarget }
    ];

    const categories = isCrypto ? cryptoCategories : fiatCategories;

    // Price scenarios for crypto
    const priceScenarios = [
        { id: 'bear', label: lang === 'tr' ? 'Ayı Piyasası (-30%)' : 'Bear Market (-30%)', multiplier: 0.7, icon: LuSnowflake, color: '#ff4444' },
        { id: 'neutral', label: lang === 'tr' ? 'Nötr (Mevcut)' : 'Neutral (Current)', multiplier: 1, icon: LuActivity, color: '#9ca3af' },
        { id: 'bull', label: lang === 'tr' ? 'Boğa Piyasası (+50%)' : 'Bull Market (+50%)', multiplier: 1.5, icon: LuFlame, color: '#00ff88' },
        { id: 'superbull', label: lang === 'tr' ? 'Süper Boğa (+100%)' : 'Super Bull (+100%)', multiplier: 2, icon: LuTrendingUp, color: '#00d4ff' },
    ];

    // Get selected coin data
    const getSelectedCoin = (symbol) => {
        return coins.find(c => c.symbol === symbol);
    };

    useEffect(() => {
        const key = isCrypto ? 'cryptoGoals' : 'investmentGoals';
        localStorage.setItem(key, JSON.stringify(goals));
    }, [goals, isCrypto]);

    // Calculate projection for fiat goals
    const calculateFiatProjection = (goal) => {
        const current = parseFloat(goal.currentAmount) || 0;
        const target = parseFloat(goal.targetAmount) || 0;
        const monthly = parseFloat(goal.monthlyContribution) || 0;
        const monthlyReturn = (parseFloat(goal.expectedReturn) || 0) / 12 / 100;

        if (target <= current) {
            return { months: 0, projections: [{ month: 0, value: current }], achieved: true };
        }

        const projections = [];
        let balance = current;
        let months = 0;

        while (balance < target && months < 600) {
            projections.push({ month: months, value: balance });
            balance = balance * (1 + monthlyReturn) + monthly;
            months++;
        }
        projections.push({ month: months, value: Math.min(balance, target) });

        return { months, projections, achieved: balance >= target };
    };

    // Calculate projection for crypto goals
    const calculateCryptoProjection = (goal) => {
        const coin = getSelectedCoin(goal.selectedCoin);
        const currentPrice = coin?.price || 0;
        const scenario = priceScenarios.find(s => s.id === goal.priceScenario) || priceScenarios[1];
        const projectedPrice = currentPrice * scenario.multiplier;

        const targetCoin = parseFloat(goal.targetCoinAmount) || 0;
        const currentCoin = parseFloat(goal.currentCoinAmount) || 0;
        const weeklyCoin = parseFloat(goal.weeklyCoinAmount) || 0;

        if (targetCoin <= currentCoin || weeklyCoin <= 0) {
            const weeks = targetCoin <= currentCoin ? 0 : Infinity;
            return {
                weeks,
                months: Math.ceil(weeks / 4),
                currentCoin,
                targetCoin,
                currentValue: currentCoin * currentPrice,
                targetValue: targetCoin * projectedPrice,
                projectedPrice,
                scenario,
                projections: [{ week: 0, coins: currentCoin, value: currentCoin * currentPrice }],
                achieved: targetCoin <= currentCoin
            };
        }

        const projections = [];
        let coinBalance = currentCoin;
        let weeks = 0;

        while (coinBalance < targetCoin && weeks < 520) { // Max 10 years
            const currentValue = coinBalance * currentPrice;
            projections.push({ week: weeks, coins: coinBalance, value: currentValue });
            coinBalance += weeklyCoin;
            weeks++;
        }
        projections.push({ week: weeks, coins: Math.min(coinBalance, targetCoin), value: Math.min(coinBalance, targetCoin) * projectedPrice });

        // Calculate total cost (DCA)
        const totalCost = weeks * weeklyCoin * currentPrice;
        const avgCost = totalCost / targetCoin;

        return {
            weeks,
            months: Math.ceil(weeks / 4),
            currentCoin,
            targetCoin,
            currentValue: currentCoin * currentPrice,
            targetValue: targetCoin * projectedPrice,
            projectedPrice,
            scenario,
            projections,
            achieved: coinBalance >= targetCoin,
            totalCost,
            avgCost,
            weeklyCost: weeklyCoin * currentPrice
        };
    };

    const calculateProjection = (goal) => {
        return isCrypto ? calculateCryptoProjection(goal) : calculateFiatProjection(goal);
    };

    const handleAddGoal = (e) => {
        e.preventDefault();
        if (!newGoal.name) return;
        if (isCrypto && !newGoal.targetCoinAmount) return;
        if (!isCrypto && !newGoal.targetAmount) return;

        const goal = { id: Date.now(), ...newGoal, createdAt: new Date().toISOString() };
        setGoals([...goals, goal]);
        setNewGoal({
            name: '',
            targetAmount: '',
            targetCoinAmount: '',
            currentAmount: '',
            currentCoinAmount: '',
            monthlyContribution: '',
            weeklyCoinAmount: '',
            expectedReturn: isCrypto ? '0' : '8',
            deadline: '',
            category: isCrypto ? 'hodl' : 'savings',
            selectedCoin: 'BTCUSDT',
            priceScenario: 'neutral'
        });
        setShowForm(false);
    };

    const handleDeleteGoal = (id) => setGoals(goals.filter(g => g.id !== id));

    const getProgress = (goal) => {
        if (isCrypto) {
            const current = parseFloat(goal.currentCoinAmount) || 0;
            const target = parseFloat(goal.targetCoinAmount) || 1;
            return Math.min((current / target) * 100, 100);
        }
        const current = parseFloat(goal.currentAmount) || 0;
        const target = parseFloat(goal.targetAmount) || 1;
        return Math.min((current / target) * 100, 100);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(value);
    };

    const formatCoin = (value, symbol) => {
        const coin = getSelectedCoin(symbol);
        const shortName = coin?.shortName || symbol?.replace('USDT', '') || '';
        return `${parseFloat(value).toFixed(4)} ${shortName}`;
    };

    const formatMonths = (months) => {
        const years = Math.floor(months / 12);
        const rem = months % 12;
        if (years === 0) return rem + (lang === 'tr' ? ' ay' : ' months');
        if (rem === 0) return years + (lang === 'tr' ? ' yıl' : ' years');
        return years + (lang === 'tr' ? ' yıl ' : 'y ') + rem + (lang === 'tr' ? ' ay' : 'm');
    };

    const formatWeeks = (weeks) => {
        if (weeks === Infinity) return lang === 'tr' ? 'Belirsiz' : 'Indefinite';
        const months = Math.floor(weeks / 4);
        const remWeeks = weeks % 4;
        if (months === 0) return weeks + (lang === 'tr' ? ' hafta' : ' weeks');
        if (remWeeks === 0) return months + (lang === 'tr' ? ' ay' : ' months');
        return months + (lang === 'tr' ? ' ay ' : 'm ') + remWeeks + (lang === 'tr' ? ' hafta' : 'w');
    };

    const [selectedGoal, setSelectedGoal] = useState(null);

    const getChartData = (goal) => {
        if (!goal) return null;
        const projection = calculateProjection(goal);

        if (isCrypto) {
            const sampledData = projection.projections.filter((_, i) =>
                i === 0 || i === projection.projections.length - 1 || i % 4 === 0
            );

            return {
                labels: sampledData.map(p => p.week === 0 ? (lang === 'tr' ? 'Başlangıç' : 'Start') : Math.floor(p.week / 4) + 'ay'),
                datasets: [
                    {
                        label: lang === 'tr' ? 'Coin Miktarı' : 'Coin Amount',
                        data: sampledData.map(p => p.coins),
                        borderColor: '#00ff88',
                        backgroundColor: 'rgba(0, 255, 136, 0.1)',
                        fill: true,
                        tension: 0.4,
                        yAxisID: 'y'
                    },
                    {
                        label: lang === 'tr' ? 'Hedef' : 'Target',
                        data: sampledData.map(() => parseFloat(goal.targetCoinAmount)),
                        borderColor: '#00d4ff',
                        borderDash: [5, 5],
                        fill: false,
                        pointRadius: 0,
                        yAxisID: 'y'
                    }
                ]
            };
        }

        const sampledData = projection.projections.filter((_, i) =>
            i === 0 || i === projection.projections.length - 1 || i % 12 === 0
        );

        return {
            labels: sampledData.map(p => p.month === 0 ? (lang === 'tr' ? 'Başlangıç' : 'Start') : Math.floor(p.month / 12) + 'y'),
            datasets: [
                { label: lang === 'tr' ? 'Projeksiyon' : 'Projection', data: sampledData.map(p => p.value), borderColor: '#00d4ff', backgroundColor: 'rgba(0, 212, 255, 0.1)', fill: true, tension: 0.4 },
                { label: lang === 'tr' ? 'Hedef' : 'Target', data: sampledData.map(() => parseFloat(goal.targetAmount)), borderColor: '#00ff88', borderDash: [5, 5], fill: false, pointRadius: 0 }
            ]
        };
    };

    const chartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'top', labels: { color: '#9ca3af' } } },
        scales: {
            x: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af' } },
            y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#9ca3af', callback: (value) => isCrypto ? value.toFixed(2) : formatCurrency(value) } }
        }
    };

    // Get coin icon
    const getCoinIcon = (symbol) => {
        const coin = getSelectedCoin(symbol);
        if (!coin) return null;
        return `https://assets.coincap.io/assets/icons/${coin.shortName.toLowerCase()}@2x.png`;
    };

    return (
        <div className="goal-planning-page">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">
                                {isCrypto
                                    ? (lang === 'tr' ? 'Kripto Hedef Planlama' : 'Crypto Goal Planning')
                                    : (lang === 'tr' ? 'Hedef Planlama' : 'Goal Planning')
                                }
                            </h1>
                        </div>
                    </div>
                    {isCrypto && connected && (
                        <div className="page-actions">
                            <span className="live-indicator">
                                <LuZap className="live-icon" />
                                <span>{lang === 'tr' ? 'Canlı Veri' : 'Live Data'}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="goal-actions">
                <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
                    <LuPlus /> {lang === 'tr' ? 'Yeni Hedef' : 'New Goal'}
                </button>
            </div>

            {showForm && (
                <div className="card goal-form-card">
                    <h3>{isCrypto ? (lang === 'tr' ? 'Yeni Kripto Hedefi' : 'New Crypto Goal') : (lang === 'tr' ? 'Yeni Hedef Oluştur' : 'Create New Goal')}</h3>
                    <form onSubmit={handleAddGoal}>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>{lang === 'tr' ? 'Hedef Adı' : 'Goal Name'}</label>
                                <input type="text" className="form-input" value={newGoal.name} onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })} placeholder={isCrypto ? (lang === 'tr' ? 'örn: 1 BTC Biriktir' : 'e.g., Accumulate 1 BTC') : ''} required />
                            </div>
                            <div className="form-group">
                                <label>{lang === 'tr' ? 'Kategori' : 'Category'}</label>
                                <select className="form-input" value={newGoal.category} onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })}>
                                    {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.label}</option>))}
                                </select>
                            </div>

                            {isCrypto ? (
                                <>
                                    <div className="form-group full-width">
                                        <label><LuBitcoin /> {lang === 'tr' ? 'Coin Seçin' : 'Select Coin'}</label>
                                        <CoinSelector
                                            coins={coins}
                                            selectedCoin={newGoal.selectedCoin}
                                            onSelect={(symbol) => setNewGoal({ ...newGoal, selectedCoin: symbol })}
                                            lang={lang}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><LuTarget /> {lang === 'tr' ? 'Hedef Coin Miktarı' : 'Target Coin Amount'}</label>
                                        <input type="number" className="form-input" value={newGoal.targetCoinAmount} onChange={(e) => setNewGoal({ ...newGoal, targetCoinAmount: e.target.value })} step="0.0001" placeholder="1.0" required />
                                        {newGoal.targetCoinAmount && newGoal.selectedCoin && (
                                            <span className="form-hint">
                                                ≈ {formatCurrency(parseFloat(newGoal.targetCoinAmount || 0) * (getSelectedCoin(newGoal.selectedCoin)?.price || 0))}
                                            </span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label><LuPiggyBank /> {lang === 'tr' ? 'Mevcut Coin Miktarı' : 'Current Coin Amount'}</label>
                                        <input type="number" className="form-input" value={newGoal.currentCoinAmount} onChange={(e) => setNewGoal({ ...newGoal, currentCoinAmount: e.target.value })} step="0.0001" placeholder="0" />
                                    </div>
                                    <div className="form-group">
                                        <label><LuRepeat /> {lang === 'tr' ? 'Haftalık Alım (DCA)' : 'Weekly Purchase (DCA)'}</label>
                                        <input type="number" className="form-input" value={newGoal.weeklyCoinAmount} onChange={(e) => setNewGoal({ ...newGoal, weeklyCoinAmount: e.target.value })} step="0.0001" placeholder="0.01" />
                                        {newGoal.weeklyCoinAmount && newGoal.selectedCoin && (
                                            <span className="form-hint">
                                                ≈ {formatCurrency(parseFloat(newGoal.weeklyCoinAmount || 0) * (getSelectedCoin(newGoal.selectedCoin)?.price || 0))}/hafta
                                            </span>
                                        )}
                                    </div>
                                    <div className="form-group full-width">
                                        <label>{lang === 'tr' ? 'Fiyat Senaryosu' : 'Price Scenario'}</label>
                                        <div className="scenario-buttons">
                                            {priceScenarios.map(scenario => {
                                                const Icon = scenario.icon;
                                                return (
                                                    <button
                                                        key={scenario.id}
                                                        type="button"
                                                        className={`scenario-btn ${newGoal.priceScenario === scenario.id ? 'active' : ''}`}
                                                        style={{ '--scenario-color': scenario.color }}
                                                        onClick={() => setNewGoal({ ...newGoal, priceScenario: scenario.id })}
                                                    >
                                                        <Icon /> {scenario.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="form-group">
                                        <label><LuDollarSign /> {lang === 'tr' ? 'Hedef Tutar' : 'Target Amount'}</label>
                                        <input type="number" className="form-input" value={newGoal.targetAmount} onChange={(e) => setNewGoal({ ...newGoal, targetAmount: e.target.value })} required />
                                    </div>
                                    <div className="form-group">
                                        <label><LuPiggyBank /> {lang === 'tr' ? 'Mevcut Birikim' : 'Current Savings'}</label>
                                        <input type="number" className="form-input" value={newGoal.currentAmount} onChange={(e) => setNewGoal({ ...newGoal, currentAmount: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label><LuCalendar /> {lang === 'tr' ? 'Aylık Katkı' : 'Monthly Contribution'}</label>
                                        <input type="number" className="form-input" value={newGoal.monthlyContribution} onChange={(e) => setNewGoal({ ...newGoal, monthlyContribution: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label><LuPercent /> {lang === 'tr' ? 'Beklenen Yıllık Getiri (%)' : 'Expected Annual Return (%)'}</label>
                                        <input type="number" className="form-input" value={newGoal.expectedReturn} onChange={(e) => setNewGoal({ ...newGoal, expectedReturn: e.target.value })} step="0.1" />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{lang === 'tr' ? 'İptal' : 'Cancel'}</button>
                            <button type="submit" className="btn btn-primary"><LuCheck /> {lang === 'tr' ? 'Hedef Oluştur' : 'Create Goal'}</button>
                        </div>
                    </form>
                </div>
            )}

            {goals.length === 0 ? (
                <div className="empty-state">
                    <LuSparkles className="empty-icon" />
                    <h3>{lang === 'tr' ? 'Henüz hedef yok' : 'No goals yet'}</h3>
                    <p>{isCrypto ? (lang === 'tr' ? 'İlk kripto birikim hedefinizi oluşturun' : 'Create your first crypto accumulation goal') : (lang === 'tr' ? 'İlk finansal hedefinizi oluşturun' : 'Create your first financial goal')}</p>
                </div>
            ) : (
                <div className="goals-grid">
                    {goals.map(goal => {
                        const progress = getProgress(goal);
                        const projection = calculateProjection(goal);
                        const CategoryIcon = categories.find(c => c.id === goal.category)?.icon || LuTarget;
                        const coin = isCrypto ? getSelectedCoin(goal.selectedCoin) : null;

                        return (
                            <div key={goal.id} className={'goal-card ' + (selectedGoal?.id === goal.id ? 'selected' : '')} onClick={() => setSelectedGoal(selectedGoal?.id === goal.id ? null : goal)}>
                                <div className="goal-header">
                                    <div className="goal-icon">
                                        {isCrypto && coin ? (
                                            <img src={getCoinIcon(goal.selectedCoin)} alt={coin.shortName} className="goal-coin-icon" onError={(e) => e.target.style.display = 'none'} />
                                        ) : (
                                            <CategoryIcon />
                                        )}
                                    </div>
                                    <div className="goal-info">
                                        <h3>{goal.name}</h3>
                                        <span className="goal-category">{categories.find(c => c.id === goal.category)?.label}</span>
                                    </div>
                                    <button className="btn-icon danger" onClick={(e) => { e.stopPropagation(); handleDeleteGoal(goal.id); }}><LuTrash2 /></button>
                                </div>

                                {isCrypto ? (
                                    <>
                                        <div className="goal-amounts crypto">
                                            <div className="amount-current">
                                                <span className="label">{lang === 'tr' ? 'Mevcut' : 'Current'}</span>
                                                <span className="value coin-value">{formatCoin(goal.currentCoinAmount || 0, goal.selectedCoin)}</span>
                                                <span className="usd-value">{formatCurrency(projection.currentValue)}</span>
                                            </div>
                                            <div className="amount-target">
                                                <span className="label">{lang === 'tr' ? 'Hedef' : 'Target'}</span>
                                                <span className="value coin-value">{formatCoin(goal.targetCoinAmount, goal.selectedCoin)}</span>
                                                <span className="usd-value scenario" style={{ color: projection.scenario?.color }}>
                                                    {formatCurrency(projection.targetValue)} ({projection.scenario?.label?.split('(')[1]?.replace(')', '') || 'Nötr'})
                                                </span>
                                            </div>
                                        </div>
                                        <div className="goal-progress">
                                            <div className="progress-bar"><div className="progress-fill crypto" style={{ width: progress + '%' }} /></div>
                                            <span className="progress-text">{progress.toFixed(1)}%</span>
                                        </div>
                                        <div className="goal-stats crypto">
                                            <div className="stat">
                                                <LuClock />
                                                <span>{projection.achieved ? (lang === 'tr' ? 'Hedefe ulaşıldı!' : 'Goal achieved!') : formatWeeks(projection.weeks)}</span>
                                            </div>
                                            {goal.weeklyCoinAmount && (
                                                <div className="stat">
                                                    <LuRepeat />
                                                    <span>{formatCurrency(projection.weeklyCost)}/hafta</span>
                                                </div>
                                            )}
                                            {projection.totalCost > 0 && (
                                                <div className="stat">
                                                    <LuDollarSign />
                                                    <span>{lang === 'tr' ? 'Toplam:' : 'Total:'} {formatCurrency(projection.totalCost)}</span>
                                                </div>
                                            )}
                                        </div>
                                        {coin && (
                                            <div className="goal-live-price">
                                                <span className="label">{lang === 'tr' ? 'Anlık Fiyat:' : 'Live Price:'}</span>
                                                <span className={`price ${coin.priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
                                                    {formatCurrency(coin.price)}
                                                    <small>({coin.priceChangePercent >= 0 ? '+' : ''}{coin.priceChangePercent.toFixed(2)}%)</small>
                                                </span>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="goal-amounts">
                                            <div className="amount-current">
                                                <span className="label">{lang === 'tr' ? 'Mevcut' : 'Current'}</span>
                                                <span className="value">{formatCurrency(goal.currentAmount || 0)}</span>
                                            </div>
                                            <div className="amount-target">
                                                <span className="label">{lang === 'tr' ? 'Hedef' : 'Target'}</span>
                                                <span className="value">{formatCurrency(goal.targetAmount)}</span>
                                            </div>
                                        </div>
                                        <div className="goal-progress">
                                            <div className="progress-bar"><div className="progress-fill" style={{ width: progress + '%' }} /></div>
                                            <span className="progress-text">{progress.toFixed(1)}%</span>
                                        </div>
                                        <div className="goal-stats">
                                            <div className="stat">
                                                <LuClock />
                                                <span>{projection.achieved && projection.months === 0 ? (lang === 'tr' ? 'Hedefe ulaşıldı!' : 'Goal achieved!') : formatMonths(projection.months)}</span>
                                            </div>
                                            {goal.monthlyContribution && (
                                                <div className="stat">
                                                    <LuCalendar />
                                                    <span>{formatCurrency(goal.monthlyContribution)}/ay</span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedGoal && (
                <div className="card goal-chart-card">
                    <h3><LuChartLine /> {selectedGoal.name} - {lang === 'tr' ? 'Projeksiyon' : 'Projection'}</h3>
                    <div className="chart-container" style={{ height: '300px' }}>
                        <Line data={getChartData(selectedGoal)} options={chartOptions} />
                    </div>
                    {isCrypto && (
                        <div className="projection-summary">
                            {(() => {
                                const proj = calculateCryptoProjection(selectedGoal);
                                return (
                                    <div className="summary-cards">
                                        <div className="summary-card">
                                            <span className="label">{lang === 'tr' ? 'Tahmini Süre' : 'Est. Duration'}</span>
                                            <span className="value">{formatWeeks(proj.weeks)}</span>
                                        </div>
                                        <div className="summary-card">
                                            <span className="label">{lang === 'tr' ? 'Toplam Maliyet' : 'Total Cost'}</span>
                                            <span className="value">{formatCurrency(proj.totalCost || 0)}</span>
                                        </div>
                                        <div className="summary-card">
                                            <span className="label">{lang === 'tr' ? 'Ort. Maliyet/Coin' : 'Avg Cost/Coin'}</span>
                                            <span className="value">{formatCurrency(proj.avgCost || 0)}</span>
                                        </div>
                                        <div className="summary-card highlight" style={{ '--highlight-color': proj.scenario?.color }}>
                                            <span className="label">{lang === 'tr' ? 'Hedef Değer' : 'Target Value'} ({proj.scenario?.label?.split(' ')[0]})</span>
                                            <span className="value">{formatCurrency(proj.targetValue)}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default GoalPlanning;
